import {
  Home,
  Activity,
  MessageSquare,
  Bell,
  FolderKanban,
  FileText,
  Users,
  Store,
  Star,
  CreditCard,
  Receipt,
  Settings,
  Shield,
  LifeBuoy,
  BookOpen,
  Book,
  FileCode2,
  GraduationCap,
  Headphones,
  Smartphone,
  Laptop,
  BrainCircuit,
  Handshake,
  Building2,
  ShoppingCart,
  Package,
  ClipboardList,
  Hammer,
  Truck,
  Boxes,
} from "lucide-react";

export const LOGO_URL = "/logo-construlink.png";

export const HERO_IMAGE = "/hero/construlink.png";

export const TOP_NAV = [
  { label: "Inicio", to: "/" },
  { label: "Red", to: "/conexion" },
  { label: "Software", to: "/software" },
  { label: "Academia", to: "/academia" },
  { label: "Marketplace", to: "/tienda" },
  { label: "IA", to: "/ia" },
];

export const SIDEBAR_GROUPS = [
  {
    title: "General",
    items: [
      { label: "Inicio", to: "/", icon: Home, key: "inicio" },
      { label: "Actividad reciente", to: "/actividad", icon: Activity, key: "actividad" },
      { label: "Mensajes", to: "/mensajes", icon: MessageSquare, key: "mensajes" },
      { label: "Notificaciones", to: "/notificaciones", icon: Bell, key: "notificaciones" },
    ],
  },
  {
    title: "Mi Negocio",
    items: [
      { label: "Mis pedidos", to: "/proyectos", icon: Package, key: "proyectos" },
      { label: "Mis cotizaciones", to: "/cotizaciones", icon: FileText, key: "cotizaciones" },
      { label: "Mis clientes", to: "/clientes", icon: Users, key: "clientes" },
      { label: "Mis proveedores", to: "/proveedores", icon: Store, key: "proveedores" },
      { label: "Favoritos", to: "/favoritos", icon: Star, key: "favoritos" },
    ],
  },
  {
    title: "Administración",
    items: [
      { label: "Planes y Membresías", to: "/planes", icon: CreditCard, key: "planes" },
      { label: "Pagos y Facturación", to: "/pagos", icon: Receipt, key: "pagos" },
      { label: "Configuración", to: "/configuracion", icon: Settings, key: "configuracion" },
      { label: "Seguridad", to: "/seguridad", icon: Shield, key: "seguridad" },
    ],
  },
  {
    title: "Ayuda",
    items: [
      { label: "Manual ConstruLink", to: "/manual", icon: Book, key: "manual" },
      { label: "Manual Técnico", to: "/ayuda/manual-tecnico", icon: FileCode2, key: "manual-tecnico", adminOnly: true },
      { label: "Centro de Ayuda", to: "/centro-ayuda", icon: LifeBuoy, key: "centro-ayuda" },
      { label: "Soporte", to: "/soporte", icon: Headphones, key: "soporte" },
    ],
  },
];

// Grupo "Mi Negocio" dinámico según el rol del usuario.
const MI_NEGOCIO_POR_ROL = {
  cliente: {
    title: "Mi Actividad",
    items: [
      { label: "Mis pedidos", to: "/proyectos", icon: Package, key: "obras" },
      { label: "Mis profesionales", to: "/proveedores", icon: Users, key: "mis-profesionales" },
      { label: "Favoritos", to: "/favoritos", icon: Star, key: "favoritos" },
    ],
  },
  ferreteria: {
    title: "Mi Negocio",
    items: [
      { label: "Mis pedidos", to: "/proyectos", icon: Truck, key: "pedidos" },
      { label: "Mi inventario", to: "/inventario", icon: Boxes, key: "inventario" },
      { label: "Mis clientes", to: "/clientes", icon: Users, key: "clientes" },
      { label: "Cotizaciones", to: "/cotizaciones", icon: FileText, key: "cotizaciones-ferre" },
      { label: "Favoritos", to: "/favoritos", icon: Star, key: "favoritos" },
    ],
  },
  maestro: {
    title: "Mi Negocio",
    items: [
      { label: "Mis pedidos", to: "/proyectos", icon: Package, key: "proyectos" },
      { label: "Mis cotizaciones", to: "/cotizaciones", icon: FileText, key: "cotizaciones" },
      { label: "Mis clientes", to: "/clientes", icon: Users, key: "clientes" },
      { label: "Materiales", to: "/proveedores", icon: Package, key: "materiales" },
      { label: "Favoritos", to: "/favoritos", icon: Star, key: "favoritos" },
    ],
  },
};
// Profesional y contratista comparten la vista "Mi Negocio" por defecto.
MI_NEGOCIO_POR_ROL.profesional = SIDEBAR_GROUPS[1];
MI_NEGOCIO_POR_ROL.contratista = SIDEBAR_GROUPS[1];

/**
 * Devuelve los grupos del sidebar adaptados al rol del usuario.
 * Solo el grupo "Mi Negocio" cambia; el resto permanece estable.
 */
export function getSidebarGroups(role) {
  const negocio = MI_NEGOCIO_POR_ROL[role] || SIDEBAR_GROUPS[1];
  const groups = [SIDEBAR_GROUPS[0], negocio, SIDEBAR_GROUPS[2], SIDEBAR_GROUPS[3]];
  // Filtra ítems marcados como adminOnly (ej. Manual Técnico) para roles no admin.
  return groups.map((g) => ({
    ...g,
    items: (g.items || []).filter((it) => !it.adminOnly || role === "admin"),
  }));
}

export const SOLUTIONS = [
  {
    id: "conexion",
    number: "01",
    title: "ConstruLink Red",
    tagline: "CONSTRULINK RED",
    color: "#2563EB",
    tint: "rgba(37, 99, 235, 0.10)",
    Icon: Smartphone,
    iconImage: "/pilares/red.png",
    subtitle: "Conecta personas y oportunidades.",
    description: "Encuentra clientes, maestros, contratistas, ingenieros, arquitectos y ferreterías en una sola plataforma profesional.",
    features: ["Encuentra profesionales", "Publica proyectos", "Chat en tiempo real", "Búsqueda por ubicación"],
    cta: "Ir a Red",
    path: "/conexion",
  },
  {
    id: "software",
    number: "02",
    title: "ConstruLink Software",
    tagline: "CONSTRULINK SOFTWARE",
    color: "#16A34A",
    tint: "rgba(22, 163, 74, 0.10)",
    Icon: Laptop,
    iconImage: "/pilares/software.png",
    subtitle: "Optimiza la gestión de tus proyectos.",
    description: "Descarga plantillas, formatos y herramientas digitales para planificar, controlar y optimizar proyectos de construcción.",
    features: ["Presupuestos", "Metrados y APU", "Valorizaciones", "Control de obras"],
    cta: "Ir al Software",
    path: "/software",
  },
  {
    id: "academia",
    number: "03",
    title: "ConstruLink Academia",
    tagline: "CONSTRULINK ACADEMIA",
    color: "#EA580C",
    tint: "rgba(234, 88, 12, 0.10)",
    Icon: GraduationCap,
    iconImage: "/pilares/academia.jpeg",
    subtitle: "Aprende y fortalece tus competencias.",
    description: "Accede a cursos, diplomados, talleres y contenido especializado para fortalecer tus conocimientos técnicos.",
    features: ["Cursos especializados", "Diplomados", "Certificaciones", "Biblioteca técnica"],
    cta: "Ir a la Academia",
    path: "/academia",
  },
  {
    id: "marketplace",
    number: "04",
    title: "ConstruLink Marketplace",
    tagline: "CONSTRULINK MARKETPLACE",
    color: "#D97706",
    tint: "rgba(217, 119, 6, 0.10)",
    Icon: ShoppingCart,
    iconImage: "/pilares/marketplace-digital.png",
    subtitle: "Compra y vende recursos digitales.",
    description: "Comercializa y adquiere software, plantillas, libros, cursos y herramientas digitales para la industria de la construcción.",
    features: ["Plantillas profesionales", "Software técnico", "Libros digitales", "Recursos BIM y CAD"],
    cta: "Ir al Marketplace",
    path: "/tienda",
  },
  {
    id: "ia",
    number: "05",
    title: "ConstruLink IA",
    tagline: "CONSTRULINK IA",
    color: "#7E22CE",
    tint: "rgba(126, 34, 206, 0.10)",
    Icon: BrainCircuit,
    iconImage: "/pilares/ia.png",
    subtitle: "Potencia tus decisiones con Inteligencia Artificial.",
    description: "Automatiza procesos, analiza información técnica y mejora la toma de decisiones con asistentes inteligentes.",
    features: ["Análisis de presupuestos", "Generación de APU", "Asistente técnico", "Recomendaciones inteligentes"],
    cta: "Ir a la IA",
    path: "/ia",
    proximamente: true,
  },
];

export const QUICK_ACCESS = [
  { label: "Proyectos", color: "#DC2626" },
  { label: "Profesionales", color: "#1E3A8A" },
  { label: "Empresas", color: "#0891B2" },
  { label: "Materiales", color: "#EA580C" },
  { label: "Cursos", color: "#16A34A" },
  { label: "Software", color: "#2563EB" },
  { label: "Documentos", color: "#7E22CE" },
];

export const FOOTER_STATS = [
  { label: "Proyectos publicados", value: "+12,500", color: "#7E22CE" },
  { label: "Profesionales activos", value: "+8,750", color: "#1E3A8A" },
  { label: "Ferreterías registradas", value: "+2,350", color: "#DC2626" },
  { label: "Usuarios en el ecosistema", value: "+45,000", color: "#0891B2" },
  { label: "Cotizaciones generadas", value: "+28,000", color: "#EA580C" },
  { label: "Departamentos cubiertos", value: "24", color: "#16A34A" },
];

// Extended metadata for placeholder pages (mirrors sidebar items)
export const PLACEHOLDER_META = {
  actividad: { title: "Actividad reciente", subtitle: "Todo lo que sucede en tu ecosistema ConstruLink en tiempo real.", color: "#1E3A8A", stats: [["Eventos hoy", "128"], ["Interacciones", "1,245"], ["Alertas", "6"]] },
  mensajes: { title: "Mensajes", subtitle: "Bandeja unificada para clientes, contratistas y ferreterías.", color: "#2563EB", stats: [["Conversaciones", "18"], ["Sin leer", "5"], ["Archivadas", "24"]] },
  notificaciones: { title: "Notificaciones", subtitle: "Centro de notificaciones unificado del Ecosistema.", color: "#DC2626", stats: [["Nuevas", "3"], ["Esta semana", "42"], ["Silenciadas", "8"]] },
  proyectos: { title: "Mis proyectos", subtitle: "Gestiona todos los proyectos publicados en ConstruLink.", color: "#1E3A8A", stats: [["Publicados", "12"], ["En proceso", "3"], ["Finalizados", "9"]] },
  cotizaciones: { title: "Mis cotizaciones", subtitle: "Revisa y compara las cotizaciones que has recibido.", color: "#EA580C", stats: [["Recibidas", "8"], ["Aceptadas", "2"], ["En revisión", "4"]] },
  clientes: { title: "Mis clientes", subtitle: "Directorio de clientes conectados a tu cuenta.", color: "#0891B2", stats: [["Activos", "34"], ["Nuevos", "6"], ["Recurrentes", "18"]] },
  inventario: { title: "Mi inventario", subtitle: "Gestiona el stock y los materiales de tu ferretería.", color: "#EA580C", stats: [["Productos", "0"], ["Bajo stock", "0"], ["Categorías", "0"]] },
  proveedores: { title: "Mis proveedores", subtitle: "Ferreterías y proveedores frecuentes del ecosistema.", color: "#D97706", stats: [["Registrados", "22"], ["Verificados", "18"], ["Nuevos", "4"]] },
  favoritos: { title: "Favoritos", subtitle: "Guarda profesionales, empresas y materiales para acceso rápido.", color: "#FBBF24", stats: [["Guardados", "45"], ["Colecciones", "6"], ["Compartidos", "3"]] },
  planes: { title: "Planes y Membresías", subtitle: "Elige el plan ideal para tu negocio dentro del ecosistema.", color: "#FBBF24", stats: [["Free", "1"], ["Pro", "1"], ["Enterprise", "1"]] },
  pagos: { title: "Pagos y Facturación", subtitle: "Historial de pagos, facturas y métodos.", color: "#16A34A", stats: [["Este mes", "S/ 890"], ["Facturas", "12"], ["Métodos", "3"]] },
  configuracion: { title: "Configuración", subtitle: "Personaliza tu experiencia en el Ecosistema ConstruLink.", color: "#475569", stats: [["Idioma", "Español"], ["Zona horaria", "GMT-5"], ["Perfil", "85%"]] },
  seguridad: { title: "Seguridad", subtitle: "Controla accesos, sesiones y verificación en dos pasos.", color: "#0B1E36", stats: [["Sesiones", "2"], ["2FA", "Activo"], ["Alertas", "0"]] },
  verificaciones: { title: "Verificaciones", subtitle: "Verifica tu identidad, empresa y certificaciones profesionales.", color: "#16A34A", stats: [["Identidad", "Verificada"], ["Empresa", "Pendiente"], ["Certificados", "3"]] },
  "centro-ayuda": { title: "Centro de Ayuda", subtitle: "Encuentra respuestas y guías del Ecosistema ConstruLink.", color: "#0891B2", stats: [["Artículos", "128"], ["Categorías", "12"], ["Tutoriales", "34"]] },
  tutoriales: { title: "Tutoriales", subtitle: "Aprende a sacar el máximo del Ecosistema ConstruLink.", color: "#7E22CE", stats: [["Videos", "58"], ["Guías", "42"], ["Certificados", "3"]] },
  soporte: { title: "Soporte", subtitle: "Contacta con el equipo humano de ConstruLink 24/7.", color: "#DC2626", stats: [["Tickets abiertos", "0"], ["Resueltos", "128"], ["SLA", "12h"]] },
};

export const COREX_ENTRY = "/software/corex";
